import { useMemo } from 'react';

import { hasBlockingQuickBooksWarnings } from '@/modules/finance/common';

import {
  GenerateBillAddedProjectListItem,
  GenerateBillAddOverheadButtonRow,
  GenerateBillListItem,
  GenerateBillMemberOverheadListItem,
  GenerateBillSalaryAdjustmentListItem,
  GenerateBillSubRow,
  GenerateBillSubRowType,
} from '../../types';
import { getSalaryAdjustmentId } from '../../utils';

interface UseBillTableDataParams {
  members: GenerateBillListItem[] | undefined;
  customMembers?: GenerateBillListItem[];
  memberOverheads: Record<string, GenerateBillMemberOverheadListItem[]>;
  memberAddedProjects: Record<string, GenerateBillAddedProjectListItem[]>;
  isMonthlySalaryEnabledFor: (memberId: string) => boolean;
}

/**
 * Composes the rows TanStack actually renders: server members plus the user-added custom rows,
 * the derived salary-adjustment line, and the "add custom line" button row.
 *
 * Sub-row ORDER is meaningful — the adjustment closes the member's breakdown, and the button row
 * always comes last.
 */
export const useBillTableData = ({
  members,
  customMembers = [],
  memberOverheads,
  memberAddedProjects,
  isMonthlySalaryEnabledFor,
}: UseBillTableDataParams): GenerateBillListItem[] =>
  useMemo(() => {
    // A member added by hand can also arrive from the server later — a further page, or a filter
    // change that widens the result set. The server row wins: both would claim the same TanStack
    // row id, and the custom lines follow the member id onto it either way.
    const serverMemberIds = new Set((members ?? []).map(member => member.id));
    const allMembers = [
      ...(members ?? []),
      ...customMembers.filter(member => !serverMemberIds.has(member.id)),
    ];
    if (allMembers.length === 0) return [];

    return allMembers.map(member => {
      const addedProjects = memberAddedProjects[member.id] ?? [];
      const overheads = memberOverheads[member.id] ?? [];

      // Server eligibility plus the QuickBooks link gate this, matching the switch's own disabled
      // rule: a member who became ineligible after a refresh (e.g. a mid-period rate change
      // landed) can never stay in salary mode.
      const isMemberQuickBooksConnected = !hasBlockingQuickBooksWarnings(member.warnings);
      const isMonthlySalaryEnabled =
        member.canSelectMonthlySalary &&
        isMemberQuickBooksConnected &&
        isMonthlySalaryEnabledFor(member.id);

      const salaryAdjustment: GenerateBillSalaryAdjustmentListItem | null = isMonthlySalaryEnabled
        ? {
            type: GenerateBillSubRowType.SALARY_ADJUSTMENT,
            id: getSalaryAdjustmentId(member.id),
            monthlySalaryAmount: member.monthlySalaryAmount,
            // Seed only; the cell recomputes this live from watched form values.
            adjustmentAmount: member.monthlySalaryAmount,
            currency: member.currency,
          }
        : null;

      const addOverheadRow: GenerateBillAddOverheadButtonRow | null = isMemberQuickBooksConnected
        ? {
            type: GenerateBillSubRowType.ADD_OVERHEAD,
            memberId: String(member.member.id),
            currencyCode: member.currencyCode,
          }
        : null;

      const memberSubRows: GenerateBillSubRow[] = [
        ...member.memberSubRows,
        ...addedProjects,
        ...overheads,
        ...(salaryAdjustment ? [salaryAdjustment] : []),
        ...(addOverheadRow ? [addOverheadRow] : []),
      ];

      return {
        ...member,
        memberAddedProjects: addedProjects,
        memberOverheads: overheads,
        memberSubRows,
        isMonthlySalaryEnabled,
        memberSalaryAdjustment: salaryAdjustment,
      };
    });
  }, [members, customMembers, memberAddedProjects, memberOverheads, isMonthlySalaryEnabledFor]);
